import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Wrapper,
  useBreadcrumbRoutes,
  Form,
  FormTitle,
  SubContent,
  InputNumber,
  DatePicker,
  Select,
  Button,
} from '@maxtropy/components';
import {
  apiV2MicrogridResolvedEditMicrogridConfigPost,
  apiV2MicrogridResolvedGetConfigObjectListPost,
  apiV2MicrogridResolvedGetElectricityContractPost,
  apiV2MicrogridResolvedGetMicrogridConfigPost,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { Col, Row, Space } from 'antd';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import React, { useEffect, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import BsaEnergyLossItems from './BsaEnergyLossItems';

import { isNil } from 'lodash-es';

interface Props {}

const MicrogridConfig: React.FC<Props> = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const urlSearch = new URLSearchParams(window.location.search);
  const uetIds = urlSearch.get('uetIds'); // 结构id
  const configId = urlSearch.get('configId') || undefined; // 配置id
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const selectedBsaList = Form.useWatch('bsaList', form);
  const [selectedBsaIdList, setSelectedBsaIdList] = React.useState<number[]>([]);

  useEffect(() => {
    if (isNil(configId)) return;
    apiV2MicrogridResolvedGetMicrogridConfigPost({ id: Number(configId) }).then(res => {
      form.setFieldsValue({
        loadId: res.load?.objectId,
        meterId: res.meter?.objectId,
        wpsIdList: res.wpsList?.map(item => item.objectId),
        pvIdList: res.pvList?.map(item => item.objectId),
        bsaList: res.bsaList?.map(item => item.bsaId),

        bsaEnergyLossList: res.bsaList?.map(item => ({
          bsaId: item.bsaId,
          chargeLossRatio: item.chargeLossRatio,
          dischargeLossRatio: item.dischargeLossRatio,
        })),
        purchaseContractId: res.purchaseContract?.objectId,
        saleContractId: res.saleContract?.objectId,
        systemOperationDate: dayjs(res.systemOperationDate),
        transformerCapacity: res.transformerCapacity,
        gridUploadMaxPower: res.gridUploadMaxPower,
      });
      setSelectedBsaIdList((res?.bsaList ?? []).map(item => item.bsaId!));
    });
  }, [configId]);

  const { data: allList } = useRequest(
    () => apiV2MicrogridResolvedGetConfigObjectListPost({ ids: uetIds?.split(',').map(Number) }),
    {
      ready: !!uetIds,
      refreshDeps: [uetIds],
    }
  );
  const { data: contractList } = useRequest(() => apiV2MicrogridResolvedGetElectricityContractPost({}));

  const loadOptions = useMemo(() => {
    return (allList?.loadList ?? []).map(item => ({
      label: item.objectName,
      value: item.objectId,
    }));
  }, [allList]);

  const meterOptions = useMemo(() => {
    return (allList?.meterList ?? []).map(item => ({
      label: item.objectName,
      value: item.objectId,
    }));
  }, [allList]);

  const pvOptions = useMemo(() => {
    return (allList?.pvList ?? []).map(item => ({
      label: item.objectName,
      value: item.objectId,
    }));
  }, [allList]);

  const wpsOptions = useMemo(() => {
    return (allList?.wpsList ?? []).map(item => ({
      label: item.objectName,
      value: item.objectId,
    }));
  }, [allList]);

  const bsaOptions = useMemo(() => {
    return (allList?.bsaList ?? [])
      .filter(m => m.isOptional || selectedBsaIdList.includes(m.objectId!))
      .map(item => ({
        label: item.objectName!,
        value: item.objectId!,
      }));
  }, [allList, selectedBsaIdList]);

  const contractOptions = useMemo(() => {
    return (contractList?.list ?? []).map(item => ({
      label: item.objectName,
      value: item.objectId,
    }));
  }, [contractList]);

  const onFinish = (values: any) => {
    apiV2MicrogridResolvedEditMicrogridConfigPost({
      ...values,
      dispatchId: Number(id),
      configId: !isNil(configId) ? Number(configId) : undefined,
      systemOperationDate: dayjs(values.systemOperationDate).format('YYYY-MM-DD'),
    }).then(res => {
      navigate(-1);
    });
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '编辑微电网配置' }]} className="page_wrapper">
      <FormTitle title="微电网配置" />
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <SubContent title="微电网对象配置">
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item label="负载" rules={[{ required: true, message: '请选择' }]} name="loadId">
                <Select placeholder="请选择" options={loadOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="市电关口表" rules={[{ required: true, message: '请选择' }]} name="meterId">
                <Select placeholder="请选择" options={meterOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item label="风电站" name="wpsIdList">
                <Select placeholder="请选择" mode="multiple" options={wpsOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="光伏站" name="pvIdList">
                <Select placeholder="请选择" mode="multiple" options={pvOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item label="储能站" name="bsaList">
                <Select placeholder="请选择" mode="multiple" options={bsaOptions} />
              </Form.Item>
            </Col>
          </Row>
          {selectedBsaList?.length > 0 && (
            <div
              style={{ padding: 16, maxWidth: 1080, border: '1px solid var(--mx-base-border-color)', borderRadius: 2 }}
            >
              <BsaEnergyLossItems bsaOptions={bsaOptions} form={form} selectedBsaList={selectedBsaList} />
            </div>
          )}
        </SubContent>
        <SubContent title="其他配置">
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                label="微电网购电合同"
                rules={[{ required: true, message: '请选择' }]}
                name="purchaseContractId"
              >
                <Select placeholder="请选择" options={contractOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="微电网售电合同"
                rules={[{ required: true, message: '请选择' }]}
                name="saleContractId"
                tooltip={{
                  title: '一般微电网园区指光伏、风电上网合同',
                  icon: <InfoCircleOutlined style={{ color: 'var(--mx-warning-color)' }} />,
                }}
              >
                <Select placeholder="请选择" options={contractOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                label="微电网系统运行开始日期"
                rules={[{ required: true, message: '请选择' }]}
                name="systemOperationDate"
              >
                <DatePicker
                  format={'YYYY-MM-DD'}
                  style={{ width: '100%' }}
                  placeholder="请选择"
                  disabledDate={date => dayjs().isBefore(date)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="微电网关口变压器容量"
                rules={[{ required: true, message: '请输入' }]}
                name="transformerCapacity"
              >
                <InputNumber placeholder="请输入" style={{ width: '100%' }} min={0} max={99999999.99} addonAfter="kW" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item label="微电网上网最大功率" name="gridUploadMaxPower">
                <InputNumber placeholder="请输入" style={{ width: '100%' }} min={0} max={99999999.99} addonAfter="kW" />
              </Form.Item>
            </Col>
          </Row>
        </SubContent>
        <Space size={8} className="sticky-footer" style={{ zIndex: 99, paddingLeft: 32 }}>
          <Button type="primary" htmlType="submit">
            保存
          </Button>
          <Button
            onClick={() => {
              navigate(-1);
            }}
          >
            返回
          </Button>
        </Space>
      </Form>
    </Wrapper>
  );
};

export default MicrogridConfig;
